import React, { useState, useEffect, useRef, useContext } from "react"
import tw, { styled } from "twin.macro"
import { useClickOutside } from "../../../utils/hooks/useClickOutside"
import {
  UserSearchResult,
  useSearchLazyQuery,
  Maybe,
  QuestionSearchResult,
  AnswerSearchResult,
  SearchResultEntityType,
} from "../../../graphql/generated/graphql"
import Input from "../../formElements/searchInput"
import { ReactComponent as SearchSVG } from "../../../assets/nav/search.svg"
import { ReactComponent as CrossSVG } from "../../../assets/nav/cross.svg"
import { defaultUsers } from "./popularList"
import { Routes } from "../../../routes"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { Dropdown } from "./dropdown"
import { AuthContext } from "../../../utils/context"

interface StyleProps {
  visible?: boolean
  hasDropdown?: boolean
}

const SearchPosition = styled("div")(({ visible }: StyleProps) => [
  tw`fixed top-0 left-0 w-full hidden tablet-lg:(relative flex justify-center)`,
  visible && tw`block z-10`,
])
const SearchWrapper = styled("div")(({ visible }: StyleProps) => [
  tw`hidden bg-background py-3 tablet-lg:(py-0)`,
  visible && tw`block z-10`,
  tw`tablet-lg:(block flex-initial w-full max-w-screen-mobile)`,
  tw`desktop:(max-w-screen-mobile-lg) desktop-lg:max-w-[38em]`,
])
const StyledSearch = styled("div")(() => [tw`py-0 flex items-center space-x-4`])
const Mask = styled("div")(({ visible }: StyleProps) => [
  tw`absolute bg-black opacity-20 w-screen h-screen left-0 right-0 top-0 bottom-0 cursor-pointer hidden`,
  visible && tw`block tablet-lg:(hidden)`,
])
const MobileSearchIcon = styled(SearchSVG)(() => [tw`hover:cursor-pointer tablet-lg:(hidden ml-2)`])

export const SearchBar = () => {
  const { t } = useTranslation("explore")
  const { preflightData } = useContext(AuthContext)
  const router = useRouter()
  const [selected, setSelected] = useState(false)
  const [input, setInput] = useState("")
  const [focus, setFocus] = useState(-1)
  const [hasFocus, setHasFocus] = useState(false)
  const [showMobileSearch, toggleMobileSearch] = useState(false)
  const [questions, setQuestions] = useState<QuestionSearchResult[]>([])
  const [answers, setAnswers] = useState<AnswerSearchResult[]>([])
  const [users, setUsers] = useState<UserSearchResult[]>([])
  const [popularUsers, setPopularUsers] = useState<Record<string, any>[]>([])
  const search = useSearchLazyQuery
  const [searchQuery, { data: searchData }] = search()
  const response = searchData && searchData.Search.results
  const emptyPopularList = popularUsers.length < 1 || selected
  const emptySearchList = (questions.length < 1 && answers.length < 1 && users.length < 1) || selected

  const clear = () => {
    setQuestions([])
    setAnswers([])
    setUsers([])
    setPopularUsers([])
    setFocus(-1)
    setHasFocus(false)
    setSelected(false) //fix the bug can't search again after redirecting/selecting a result
  }
  const handleFocus = () => {
    setHasFocus(!hasFocus)
    setPopularUsers(defaultUsers)
  }
  const handleToggleMobileSearch = () => toggleMobileSearch(!showMobileSearch)

  const ref = useRef(null)
  useClickOutside(ref, () => clear())
  interface Typeahead {
    input: string
    timeout: undefined | ReturnType<typeof setTimeout>
  }

  const [typeahead, setTypeahead] = useState<Typeahead>({ input: "", timeout: undefined })
  const dashboardSearch = (input: string) => {
    if (typeahead.timeout) {
      clearTimeout(typeahead.timeout)
    }
    setTypeahead({
      input,
      timeout: setTimeout(async () => await searchQuery({ variables: { substring: input } }), 300),
    })
  }

  useEffect(() => {
    if (response) {
      setQuestions(
        response.filter((result) => result.entityType === SearchResultEntityType.Question) as QuestionSearchResult[],
      )
      setAnswers(
        response.filter((result) => result.entityType === SearchResultEntityType.Answer) as AnswerSearchResult[],
      )
      setUsers(response.filter((result) => result.entityType === SearchResultEntityType.User) as UserSearchResult[])
    }
    if (!input && hasFocus) {
      handleFocus()
    }
  }, [response, input])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    clear()
    setInput(event.target.value)
  }

  const handleSelect = (value: Maybe<string> | undefined, route: string, query: string) => {
    value && setInput(value)
    setSelected(true)
    if (route === Routes.ShowQuestion) {
      void router.push({ pathname: route, query: { id: query } })
    } else {
      void router.push({ pathname: route, query: { username: query } })
    }
    clear()
    toggleMobileSearch(false)
    setInput("")
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 40) {
      setFocus(focus + 1)
    }
    if (event.keyCode === 38) {
      setFocus(focus - 1)
    }
    if (event.keyCode === 13) {
      event.preventDefault()

      const results: Record<string, any>[] | UserSearchResult[] | AnswerSearchResult[] | QuestionSearchResult[] = [
        ...popularUsers,
        ...users,
        ...answers,
        ...questions,
      ]
      if (focus > -1) {
        switch (results[focus].entityType) {
          case SearchResultEntityType.User:
            return handleSelect(results[focus].name, Routes.Profile, results[focus].slug)
          case SearchResultEntityType.Answer:
            return handleSelect(input, Routes.ShowQuestion, results[focus].questionId)
          case SearchResultEntityType.Question:
            return handleSelect(input, Routes.ShowQuestion, results[focus].id)
          default:
            return handleSelect(results[focus].name, Routes.Profile, results[focus].slug)
        }
      }
    }
  }

  const dropdownProps = {
    emptySearchList,
    emptyPopularList,
    questions,
    answers,
    users,
    popularUsers,
    focus,
    setFocus,
    handleSelect,
    input,
    selected,
  }

  if (!preflightData) {
    // Skeleton
    const rectSearch = (
      <div tw="py-0 px-4 w-full tablet-lg:(max-w-screen-mobile) desktop:(max-w-screen-mobile-lg) desktop-lg:(max-w-screen-tablet)">
        <div tw="bg-base-1 w-full py-6 rounded-full"></div>
      </div>
    )
    return (
      <>
        <MobileSearchIcon />
        <SearchWrapper>{rectSearch}</SearchWrapper>
      </>
    )
  }

  return (
    <>
      <Mask visible={showMobileSearch} onClick={handleToggleMobileSearch} />
      <MobileSearchIcon onClick={handleToggleMobileSearch} />
      <SearchPosition visible={showMobileSearch}>
        <SearchWrapper visible={showMobileSearch}>
          <StyledSearch>
            <form tw="px-4 tablet-lg:(px-0) w-full flex items-center relative" ref={ref}>
              <Input
                inputValue={input}
                searchBar
                handleChange={handleChange}
                placeholder={t("search.input_placeholder")}
                name="dashboard-search"
                handleKeyUp={() => input && dashboardSearch(input)}
                handleFocus={() => {
                  !input && handleFocus()
                }}
                isFieldFocused={showMobileSearch}
                handleKeyDown={handleKeyDown}
              />
              {input && (
                <CrossSVG tw="ml-4 tablet-lg:(absolute right-6) hover:cursor-pointer" onClick={() => setInput("")} />
              )}
              <Dropdown {...dropdownProps} />
              <SearchSVG data-testid="search-icon" tw="absolute left-10 tablet-lg:(left-6) hover:cursor-pointer" />
            </form>
          </StyledSearch>
        </SearchWrapper>
      </SearchPosition>
    </>
  )
}
